import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import {
  fetchGlobalAuditLogs,
  fetchUserAuditLogs,
  fetchAccessAuditLogs,
  fetchTenantAuditLogs,
  type AuditLogFilters,
} from "@/features/identity-hub/api/audit-log.api";

// ─── Key Factory ────────────────────────────────────────────────────

export const auditLogKeys = {
  all:    ["audit-log"] as const,
  global: (filters: AuditLogFilters = {}) =>
    ["audit-log", "global", filters] as const,
  globalInfinite: (filters: AuditLogFilters = {}) =>
    ["audit-log", "global", "infinite", filters] as const,
  user: (userId: string, filters: AuditLogFilters = {}) =>
    ["audit-log", "user", userId, filters] as const,
  userInfinite: (userId: string, filters: AuditLogFilters = {}) =>
    ["audit-log", "user", userId, "infinite", filters] as const,
  access: (accessId: string, filters: AuditLogFilters = {}) =>
    ["audit-log", "access", accessId, filters] as const,
  accessInfinite: (accessId: string, filters: AuditLogFilters = {}) =>
    ["audit-log", "access", accessId, "infinite", filters] as const,
  tenant: (tenantId: string, filters: AuditLogFilters = {}) =>
    ["audit-log", "tenant", tenantId, filters] as const,
  tenantInfinite: (tenantId: string, filters: AuditLogFilters = {}) =>
    ["audit-log", "tenant", tenantId, "infinite", filters] as const,
} as const;

// ─── Query Options ──────────────────────────────────────────────────

export function globalAuditLogQueryOptions(filters: AuditLogFilters = {}) {
  return queryOptions({
    queryKey: auditLogKeys.global(filters),
    queryFn:  () => fetchGlobalAuditLogs(filters),
    staleTime: 30_000,
  });
}

export function userAuditLogQueryOptions(userId: string, filters: AuditLogFilters = {}) {
  return queryOptions({
    queryKey: auditLogKeys.user(userId, filters),
    queryFn:  () => fetchUserAuditLogs(userId, filters),
    enabled:  !!userId,
    staleTime: 30_000,
  });
}

export function accessAuditLogQueryOptions(accessId: string, filters: AuditLogFilters = {}) {
  return queryOptions({
    queryKey: auditLogKeys.access(accessId, filters),
    queryFn:  () => fetchAccessAuditLogs(accessId, filters),
    enabled:  !!accessId,
    staleTime: 30_000,
  });
}

export function tenantAuditLogQueryOptions(tenantId: string, filters: AuditLogFilters = {}) {
  return queryOptions({
    queryKey: auditLogKeys.tenant(tenantId, filters),
    queryFn:  () => fetchTenantAuditLogs(tenantId, filters),
    enabled:  !!tenantId,
    staleTime: 30_000,
  });
}

// ─── Infinite Query Options ─────────────────────────────────────────
//
// Cursor-based pagination wrappers used by surfaces that need "load more on
// scroll" behavior (the global audit-log modal and the per-scope drawer
// audit tabs). The backend already returns `{ items, nextCursor, hasMore }`
// from every audit-log endpoint, so we just thread `nextCursor` through
// `getNextPageParam` and let `useInfiniteQuery` accumulate pages.
//
// `filters` excludes `cursor` deliberately — pagination state lives in the
// query, not in the cache key, so changing `actorId` / `actionIn` /
// `fromDate` resets to the first page automatically.
//
// `keepPreviousData` keeps the loaded pages visible while new filter
// combinations refetch — the modal's filter chips swap data without
// flashing the skeleton state.

const AUDIT_INFINITE_PAGE_SIZE = 50;

type AuditInfiniteFilters = Omit<AuditLogFilters, "cursor" | "limit"> & {
  /** Per-page size. Defaults to {@link AUDIT_INFINITE_PAGE_SIZE}. */
  pageSize?: number;
};

function buildInfinitePageFilters(
  filters: AuditInfiniteFilters,
  pageParam: string | undefined,
): AuditLogFilters {
  const { pageSize, ...rest } = filters;
  return {
    ...rest,
    cursor: pageParam,
    limit:  pageSize ?? AUDIT_INFINITE_PAGE_SIZE,
  };
}

export function globalAuditLogInfiniteQueryOptions(filters: AuditInfiniteFilters = {}) {
  return infiniteQueryOptions({
    queryKey: auditLogKeys.globalInfinite(filters),
    queryFn:  ({ pageParam }) => fetchGlobalAuditLogs(buildInfinitePageFilters(filters, pageParam)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function userAuditLogInfiniteQueryOptions(userId: string, filters: AuditInfiniteFilters = {}) {
  return infiniteQueryOptions({
    queryKey: auditLogKeys.userInfinite(userId, filters),
    queryFn:  ({ pageParam }) => fetchUserAuditLogs(userId, buildInfinitePageFilters(filters, pageParam)),
    enabled:  !!userId,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function accessAuditLogInfiniteQueryOptions(accessId: string, filters: AuditInfiniteFilters = {}) {
  return infiniteQueryOptions({
    queryKey: auditLogKeys.accessInfinite(accessId, filters),
    queryFn:  ({ pageParam }) => fetchAccessAuditLogs(accessId, buildInfinitePageFilters(filters, pageParam)),
    enabled:  !!accessId,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function tenantAuditLogInfiniteQueryOptions(tenantId: string, filters: AuditInfiniteFilters = {}) {
  return infiniteQueryOptions({
    queryKey: auditLogKeys.tenantInfinite(tenantId, filters),
    queryFn:  ({ pageParam }) => fetchTenantAuditLogs(tenantId, buildInfinitePageFilters(filters, pageParam)),
    enabled:  !!tenantId,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
